<script lang="ts" setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useSearchFiltersStore } from '@/views/search/searchFiltersStore.ts';
import { PriceType } from '@/views/search/types.ts';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { faXmark } from '@fortawesome/pro-regular-svg-icons';

const { t, locale } = useI18n();
const filters = useSearchFiltersStore();

const pills = computed(() => {
  const result: Array<{
    label: string;
    remove: () => void;
    key: string;
  }> = [];

  // Category pills
  filters.appliedCategories.forEach((cat) => {
    result.push({
      label: cat.text,
      remove: () => filters.clearCategory(cat.id),
      key: `cat-${cat.id}`,
    });
  });

  // PriceType pill
  if (filters.hasPriceType) {
    result.push({
      label: filters.priceType === PriceType.ForSale ? t('views.search.pills.for-sale') : t('views.search.pills.for-rent'),
      remove: filters.clearPriceType,
      key: 'priceType',
    });
  }

  // Price range pills
  if (filters.hasPriceMin) {
    result.push({
      label: t('views.search.pills.price-min-pill', { value: filters.priceMin.formatCurrency(locale.value) }),
      remove: filters.clearPriceMin,
      key: 'priceMin',
    });
  }
  if (filters.hasPriceMax) {
    result.push({
      label: t('views.search.pills.price-max-pill', { value: filters.priceMax.formatCurrency(locale.value) }),
      remove: filters.clearPriceMax,
      key: 'priceMax',
    });
  }

  // Bedrooms
  if (filters.hasBedroomsMin) {
    result.push({
      label: t('views.search.pills.bedrooms-min-pill', { value: filters.bedroomsMin }),
      remove: filters.clearBedroomsMin,
      key: 'bedroomsMin',
    });
  }
  if (filters.hasBedroomsMax) {
    result.push({
      label: filters.bedroomsMax === 0 ? t('views.search.pills.bedrooms-studio-pill') : t('views.search.pills.bedrooms-max-pill', { value: filters.bedroomsMax }),
      remove: filters.clearBedroomsMax,
      key: 'bedroomsMax',
    });
  }

  // Bathrooms
  if (filters.hasBathroomsMin) {
    result.push({
      label: t('views.search.pills.bathrooms-min-pill', { value: filters.bathroomsMin }),
      remove: filters.clearBathroomsMin,
      key: 'bathroomsMin',
    });
  }
  if (filters.hasBathroomsMax) {
    result.push({
      label: t('views.search.pills.bathrooms-max-pill', { value: filters.bathroomsMax }),
      remove: filters.clearBathroomsMax,
      key: 'bathroomsMax',
    });
  }

  return result;
});
</script>

<template>
  <div v-if="pills.length > 0" class="mb-4 flex flex-wrap gap-2 py-2">
    <template v-for="pill in pills" :key="pill.key">
      <button class="bg-ph-green-50 text-ph-green-700 ring-ph-green-600/20 inline-flex cursor-pointer items-center rounded-md px-3 py-2 text-sm font-medium ring-1 ring-inset" @click="pill.remove">
        <span class="mr-2">{{ pill.label }}</span>
        <FontAwesomeIcon :icon="faXmark" />
      </button>
    </template>
    <!-- Show "Clear All" only if any pills -->
    <button v-if="pills.length" class="inline-flex cursor-pointer items-center rounded-md bg-gray-50 px-3 py-2 text-sm font-medium text-gray-700 ring-1 ring-gray-600/20 ring-inset" @click="filters.clearAllFilters">
      {{ t('views.search.pills.clear-all') }}
    </button>
  </div>
</template>

<style lang="css" scoped></style>
